import type { ReactNode } from 'react';

import { Divider } from '#/components/Divider/Divider';
import type { CmsApiBlock } from '#/modules/cms/api/types/CmsApiBlock';
import type { CmsApiLayout } from '#/modules/cms/api/types/CmsApiLayout';
import { canHaveHeadingDivider } from '#/modules/cms/helpers/canHaveHeadingDivider';
import { getHideFirstHeadingDivider } from '#/modules/cms/helpers/getHideFirstHeadingDivider';
import { mustHaveDivider } from '#/modules/cms/helpers/mustHaveDivider';
import { mustNotHaveDivider } from '#/modules/cms/helpers/mustNotHaveDivider';
import type { CmsMappedBlock } from '#/modules/cms/mappers/CmsMappedBlock';

import {
  CmsBlockContainerWithDividers_Box_1,
  CmsBlockContainerWithDividers_Box_2,
} from './CmsBlockContainerWithDividers.styled';
import { CmsMappedBlockContainer } from './CmsMappedBlockContainer';

type Props = {
  children: ReactNode;
  mappedBlock: CmsMappedBlock<CmsApiBlock>;
  prevBlock: CmsMappedBlock<CmsApiBlock> | undefined;
  nextBlock: CmsMappedBlock<CmsApiBlock> | undefined;
  layout: CmsApiLayout;
  index: number;
  isLastBlock: boolean;
};

export function CmsBlockContainerWithDividers({
  children,
  mappedBlock,
  prevBlock,
  nextBlock,
  layout,
  index,
  isLastBlock,
}: Props) {
  const hideFirstHeadingDivider = getHideFirstHeadingDivider({
    index,
    block: mappedBlock,
    prevBlock,
  });

  const fullWidthLayout = layout === 'FULL_WIDTH';

  return (
    <CmsMappedBlockContainer
      mappedBlock={mappedBlock}
      prevBlockHasBackground={Boolean(prevBlock?.hasBackground)}
      index={index}
      id={`cms-block-${mappedBlock.id}`}
      key={mappedBlock.id}
      fullWidthLayout={fullWidthLayout}
      showHeadingDivider={fullWidthLayout && canHaveHeadingDivider(mappedBlock)}
      hideFirstHeadingDivider={hideFirstHeadingDivider}
    >
      {fullWidthLayout &&
        index > 0 &&
        mustHaveDivider(mappedBlock) &&
        prevBlock &&
        !mustNotHaveDivider(prevBlock) &&
        !mappedBlock.hasBackground && (
          <CmsBlockContainerWithDividers_Box_2
            data-full-width-layout={fullWidthLayout ? true : undefined}
          >
            <Divider marginBottom={0} marginTop={0} />
          </CmsBlockContainerWithDividers_Box_2>
        )}

      {children}

      {fullWidthLayout &&
        !isLastBlock &&
        mustHaveDivider(mappedBlock) &&
        nextBlock &&
        !mustHaveDivider(nextBlock) &&
        !mustNotHaveDivider(nextBlock) &&
        ((!mappedBlock.hasBackground && !nextBlock.hasBackground) ||
          (mappedBlock.hasBackground && nextBlock.hasBackground)) && (
          <CmsBlockContainerWithDividers_Box_1
            data-full-width-layout={fullWidthLayout ? true : undefined}
          >
            <Divider marginBottom={0} marginTop={0} />
          </CmsBlockContainerWithDividers_Box_1>
        )}
    </CmsMappedBlockContainer>
  );
}
